import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate, redirect } from 'react-router-dom';
import ErrorPage from '../ErrorPage';
import ProtectedLayout from './ProtectedLayout';
import ProtectedRoute from './ProtectedRoute';
import AdminRoute from './AdminRoute';
import PricingRedirect from './PricingRedirect';
// Eager, not code-split: /pricing is the app's public SEO page (www.oraai.com
// /pricing redirects here). Google renders the HTML later than it fetches it,
// often across a deploy, and a lazy chunk that is gone by then lands on the
// asset-load error view, which is what gets indexed as the page (ORA-890).
import Pricing from '../pages/Pricing';
import CohortRedirect from './CohortRedirect';
import DeprecatedDailyRedirect from './DeprecatedDailyRedirect';
import ExamFeatureRouteGuard from './ExamFeatureRouteGuard';
import InstanceCapabilityRouteGuard from './InstanceCapabilityRouteGuard';
import { StudyPhase } from '../slices/study/studySlice';
import {
  blockStudyLoader,
  blockReviewLoader,
  flashcardsLoader,
  sessionSummaryLoader,
  videoStudyLoader,
} from './studyLoaders';
import { courseSessionLoader } from './courseLoaders';
import CourseStudentOnly from '../pages/Course/CourseStudentOnly';
import OsShell from '../os/OsShell';
import { mcgrawHillPlainRoutes } from '../mcgrawHill/mcgrawHillRoutes';
import { desktopLandingLoader } from '../os/landingDecision';
import { documentRedirectLoader } from '../providers/lectureDocumentSessionRequest';
import {
  OS_DESKTOP_MEDIA_QUERY,
  type OsRouteMeta,
  type OsWindowVariant,
} from '../os/osRouteMeta';

// Route handles are shared: `chatRoute` is the named route id the AI
// companion's app-context envelope reports (src/types/chatEnvelope.ts, a route
// without one reports `other`), `studyPhase` drives StudyPhaseRouteSync, and
// `os` is the window metadata the shell reads. Spread osHandle() alongside the
// others rather than replacing them.
const osHandle = (
  variant: OsWindowVariant,
  options: Omit<OsRouteMeta, 'variant'> = {}
): { os: OsRouteMeta } => ({ os: { variant, ...options } });

const Onboarding = () => import('../pages/Onboarding');
const Admin = () => import('../pages/Admin');

const WebinarLaunch = () =>
  import('../pages/Admin/WebinarLaunch/WebinarLaunch');

const GroupSurveyDetail = () =>
  import('../pages/Admin/GroupSurveyDetail/GroupSurveyDetail');

const Auth = () => import('../pages/Auth');
const Unlock = () => import('../pages/Unlock/Unlock');

const RegisterWebinar = () =>
  import('../pages/RegisterWebinar/RegisterWebinar');

const WebinarRegistrationCount = () =>
  import('../pages/WebinarRegistrationCount/WebinarRegistrationCount');

const Faculty = () => import('../pages/Faculty');
const FacultyHome = () => import('../pages/Faculty/FacultyHome');
const FacultyCohortMembers = () => import('../pages/Faculty/CohortMembers');

const FacultyCohortPerformance = () =>
  import('../pages/Faculty/CohortPerformance');

const FacultyStudentDetail = () => import('../pages/Faculty/StudentDetail');

const FacultyStudentSelfAssessmentResults = () =>
  import('../pages/Faculty/StudentSelfAssessmentResults');

const Home = () => import('../pages/Home');
const CourseSessionPage = () => import('../pages/Course/CourseSessionPage');
const CourseModulesPage = () => import('../pages/Course/CourseModulesPage');
const EmailPreferences = () => import('../pages/EmailPreferences');
const School = () => import('../pages/School');
const StudyLayout = () => import('../pages/StudyLayout');
const Flashcards = () => import('../components/ora/Study/Flashcards');
const VideoStudy = () => import('../components/ora/VideoStudy');
const SessionSummary = () => import('../components/ora/Study/SessionSummary');
const Exam = () => import('../components/ora/Study/Exam');
const Account = () => import('../pages/Account');
const Dashboard = () => import('../pages/Dashboard');
const FlashcardDecks = () => import('../pages/FlashcardDecks');
const UploadDeck = () => import('../pages/UploadDeck');
const Library = () => import('../pages/Library');
const LibraryHome = () => import('../pages/LibraryHome/LibraryHome');
const AudioOverviews = () => import('../pages/AudioOverviews/AudioOverviews');

const NclexFormatTour = () =>
  import('../pages/NclexFormatTour/NclexFormatTourPage');

const Topics = () => import('../pages/Topics');
const Videos = () => import('../pages/Videos');
const Help = () => import('../pages/Help');
// Faculty has its own auth path and route tree, so it mounts its own shell.
const FacultyShell = () => <OsShell />;

// AI Chat lives on the desktop at /. Legacy /chat and /chat-v2 links
// redirect there; query params survive so conversation and study-set return
// links keep working. A loader redirect, not a <Navigate> element, so the
// redirect resolves before anything is committed — an element would paint
// one frame of a plain, shell-less route first.
//
// Below the OS breakpoint there is no desktop: the shell forwards / to the
// Dashboard and opens the chat sheet only for a thread address, so a bare
// /chat asks for the sheet explicitly (the shell consumes the flag).
const chatRedirectLoader = ({ request }: { request: Request }) => {
  const { search } = new URL(request.url);

  if (window.matchMedia(OS_DESKTOP_MEDIA_QUERY).matches) {
    return redirect(`/${search}`);
  }

  const params = new URLSearchParams(search);
  params.set('openChat', '1');

  return redirect(`/?${params.toString()}`);
};

const Games = () => import('../pages/Games');
const Doctordle = () => import('../pages/Games/Doctordle');
const DoctordleResults = () => import('../pages/Games/Doctordle/Results');
const DoctordleArchive = () => import('../pages/Games/Doctordle/Archive');
const Daily = () => import('../pages/Games/Daily');
const GameStart = () => import('../components/ora/Games/GameStart');

const SchoolLeaderboard = () =>
  import('../pages/Games/SchoolLeaderboard/SchoolLeaderboard');

const ActivationQuestion = () =>
  import('../pages/ActivationQuestion/ActivationQuestion');

const WebinarLivePage = () => import('../pages/WebinarLive/WebinarLivePage');

const WebinarAdminConsole = () =>
  import('../pages/WebinarLive/WebinarAdminConsole');

const WebinarRedirect = () => import('../pages/WebinarLive/WebinarRedirect');

// Wraps the shared redirect with the admin flag; shaped as an import thunk so
// it plugs into lazyRoute like every other page module.
const WebinarAdminRedirect = async () => {
  const { default: Redirect } =
    await import('../pages/WebinarLive/WebinarRedirect');

  return { default: () => <Redirect admin /> };
};

const GuestSurvival = () =>
  import('../pages/Games/SchoolLeaderboard/GuestSurvival');

const Buzzwords = () => import('../pages/Games/Buzzwords');
const Differential = () => import('../pages/Games/Differential');
const Survival = () => import('../pages/Games/Survival/SurvivalPractice');

const PokemonOrDrug = () =>
  import('../pages/Games/PokemonOrDrug/PokemonOrDrugPractice');

const QBank = () => import('../pages/QBank');
const SelfAssessments = () => import('../pages/SelfAssessments');

const SelfAssessmentBlockRoute = () =>
  import('../pages/SelfAssessments/BlockRoute');

const VerifyEmail = () => import('../pages/VerifyEmail');
const InvitePage = () => import('../pages/Invite');
const LtiConnect = () => import('../pages/LtiConnect');
const ResetPassword = () => import('../pages/Auth/ResetPassword');
const TwoFactor = () => import('../pages/Auth/TwoFactor/TwoFactor');

// Route-level code splitting (React Router `lazy`): the router resolves the
// module BEFORE committing the navigation, so the previous page stays painted
// while a chunk loads — no blank Suspense flash inside the persistent window.
const lazyRoute =
  (load: () => Promise<{ default: ComponentType }>) => async () => ({
    Component: (await load()).default,
  });

/**
 * Development harness for the AI-companion thread (ORA-447), driven by the
 * recorded SSE fixtures (`pnpm chat:fixtures`). The dynamic import sits inside
 * the branch so a production build drops the route *and* its chunk.
 */
const devOnlyRoutes = import.meta.env.DEV
  ? [
      {
        path: 'dev/chat',
        lazy: lazyRoute(
          () => import('../pages/ChatCompanionDev/ChatCompanionDev')
        ),
        errorElement: <ErrorPage />,
      },
    ]
  : [];

// The Dashboard was the app's landing page before the AI desktop took /,
// and saved links to its old /home address still arrive. Without this they
// would fall through to the single-segment `:cohortId` catch-all, which
// records "home" as a cohort and lands on the desktop.
const dashboardRedirectLoader = ({ request }: { request: Request }) => {
  const { search } = new URL(request.url);

  return redirect(`/dashboard${search}`);
};

export const router = createBrowserRouter([
  {
    path: '/',
    element: <ProtectedLayout />,
    errorElement: <ErrorPage />,
    // Initial page load resolves the matched route's lazy module before the
    // first render — same blank-until-ready as the old Suspense fallback,
    // declared explicitly so the router doesn't warn for routes under it.
    HydrateFallback: () => null,
    children: [
      {
        // The AI desktop is the home route. No OS handle and no element:
        // OsShell keeps OsDesktop mounted beneath every route and shows it
        // whenever / is active with no window on top. It reports the `chat`
        // route id because it IS the full chat page. The loader sends a
        // session that opens here on to the Dashboard when the student has
        // asked for that, before the desktop can paint (ORA-751).
        index: true,
        element: null,
        loader: desktopLandingLoader,
        handle: { studyPhase: StudyPhase.HOME, chatRoute: 'chat' },
      },
      {
        path: 'home',
        loader: dashboardRedirectLoader,
      },
      ...mcgrawHillPlainRoutes,
      {
        // mcgraw-hill: the previous address stays compatible.
        // (otherwise the top-level cohort route would read it as a cohort).
        path: 'nursing-demo/*',
        element: <Navigate to="/mh" replace />,
      },
      {
        path: 'dashboard',
        lazy: lazyRoute(Home),
        handle: {
          studyPhase: StudyPhase.HOME,
          chatRoute: 'dashboard',
          ...osHandle('default', {
            title: 'Dashboard',
            hasExamSelectors: true,
            hidesMobileHeader: true,
          }),
        },
      },
      {
        // Courses V1: the course start screen is the Dashboard for the
        // `course` home variant (see `useIsCourseStudent`); the old address lands
        // there rather than on a 404. Anyone else lands on the desktop, where
        // main's cohort catch-all sent them.
        path: 'course',
        element: (
          <CourseStudentOnly>
            <Navigate to="/dashboard" replace />
          </CourseStudentOnly>
        ),
      },
      {
        // The course student's module list (Courses V1): every module with
        // what it holds and whether it is done. Reports the `course` route id
        // (the backend's course-page guidance).
        path: 'modules',
        lazy: lazyRoute(CourseModulesPage),
        handle: {
          chatRoute: 'course',
          ...osHandle('default', {
            windowGroup: 'course-modules',
            title: 'Modules',
          }),
        },
      },
      {
        // The session takes the whole screen like the study routes; the
        // companion docks beside the step (see AIDrawerRenderer's push list).
        path: 'course/session',
        lazy: lazyRoute(CourseSessionPage),
        loader: courseSessionLoader,
        handle: {
          chatRoute: 'course.session',
          ...osHandle('fullscreen', { windowGroup: 'course-session' }),
        },
      },
      {
        // No OS metadata: onboarding gates the whole app fullscreen (see
        // ProtectedRoute), so it renders plainly outside the window system
        // with its own banner, exactly as on main.
        path: 'onboarding',
        lazy: lazyRoute(Onboarding),
      },
      {
        // `admin/:tab?` matches only one segment, so the launch dashboard
        // needs its own more-specific nested path before the tab route.
        path: 'admin/webinars/:webinarId',
        lazy: async () => {
          const { default: Component } = await WebinarLaunch();

          return {
            element: (
              <AdminRoute>
                <Component />
              </AdminRoute>
            ),
          };
        },
        handle: osHandle('default', { windowGroup: 'admin', title: 'Webinar' }),
      },
      {
        path: 'admin/group-surveys/:surveyId',
        lazy: async () => {
          const { default: Component } = await GroupSurveyDetail();

          return {
            element: (
              <AdminRoute>
                <Component />
              </AdminRoute>
            ),
          };
        },
        handle: osHandle('default', { windowGroup: 'admin', title: 'Survey' }),
      },
      {
        // Optional `:tab` so both `/admin` and `/admin/webinars` resolve to the
        // same route object; the Admin page derives the active tab from it and
        // a reload stays on that tab.
        path: 'admin/:tab?',
        lazy: async () => {
          const { default: Component } = await Admin();

          return {
            element: (
              <AdminRoute>
                <Component />
              </AdminRoute>
            ),
          };
        },
        handle: osHandle('default', { windowGroup: 'admin', title: 'Admin' }),
      },
      {
        // Shares the admin window group so drilling into a school from the
        // admin dashboard crossfades instead of replaying the window entry.
        path: 'school/:schoolId',
        lazy: lazyRoute(School),
        handle: osHandle('default', { windowGroup: 'admin', title: 'School' }),
      },
      {
        path: 'study',
        lazy: lazyRoute(StudyLayout),
        handle: osHandle('fullscreen', { windowGroup: 'study' }),
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          {
            path: 'qbank',
            children: [
              // The BlockSelect screen that used to live here is gone; stale
              // URLs (old bookmarks/history) land on the Dashboard instead
              // of a 404.
              { index: true, element: <Navigate to="/dashboard" replace /> },
              {
                path: 'block',
                lazy: lazyRoute(Exam),
                loader: blockStudyLoader,
                handle: {
                  studyPhase: StudyPhase.BLOCK_STUDY,
                  chatRoute: 'study.qbank.block',
                },
              },
              {
                path: 'block/review',
                lazy: lazyRoute(Exam),
                loader: blockReviewLoader,
                handle: {
                  studyPhase: StudyPhase.BLOCK_REVIEW,
                  chatRoute: 'study.qbank.review',
                },
              },
            ],
          },
          {
            path: 'flashcards',
            lazy: lazyRoute(Flashcards),
            loader: flashcardsLoader,
            handle: {
              studyPhase: StudyPhase.FLASHCARD_STUDY,
              chatRoute: 'study.flashcards',
            },
          },
          {
            path: 'video',
            lazy: lazyRoute(VideoStudy),
            loader: videoStudyLoader,
            handle: {
              studyPhase: StudyPhase.VIDEO_STUDY,
              chatRoute: 'study.video',
            },
          },
          {
            path: 'summary',
            lazy: lazyRoute(SessionSummary),
            loader: sessionSummaryLoader,
            handle: {
              studyPhase: StudyPhase.SESSION_SUMMARY,
              chatRoute: 'study.summary',
            },
          },
        ],
      },
      {
        // A live webinar is its own flow, not a QBank block: it must not carry
        // the study route's id, whose gate assumes a loaded block.
        path: 'study/webinar/:cohort',
        lazy: lazyRoute(WebinarLivePage),
        handle: {
          studyPhase: StudyPhase.BLOCK_STUDY,
          chatRoute: 'webinar',
        },
      },
      {
        // One practice question opened from an email (ORA-948). Fullscreen,
        // like the study session it leads into: /study routes mount no
        // desktop underneath. No chat route id; it reports `other`.
        path: 'study/question/:vignetteId',
        lazy: lazyRoute(ActivationQuestion),
        handle: osHandle('fullscreen', { title: 'Practice Question' }),
      },
      {
        // The presenter's console. No chat route id; it reports `other`.
        path: 'study/webinar/:cohort/admin',
        lazy: lazyRoute(WebinarAdminConsole),
        handle: { studyPhase: StudyPhase.BLOCK_STUDY },
      },
      {
        path: 'account',
        lazy: lazyRoute(Account),
        handle: {
          chatRoute: 'account',
          ...osHandle('default', { title: 'Account' }),
        },
      },
      {
        path: 'performance',
        lazy: lazyRoute(Dashboard),
        handle: {
          chatRoute: 'performance',
          ...osHandle('default', { title: 'Performance' }),
        },
      },
      {
        // Pathless: the library pages an instance without a study library
        // never mounts. An article (library/:articleId) stays open for term
        // pop-ups and transcripts; the guard lists what it hides.
        element: <InstanceCapabilityRouteGuard />,
        children: [
          {
            path: 'flashcards',
            lazy: lazyRoute(FlashcardDecks),
            handle: {
              chatRoute: 'flashcard-decks',
              ...osHandle('default', {
                windowGroup: 'flashcards',
                title: 'Flashcards',
              }),
            },
          },
          {
            path: 'flashcards/:examId',
            lazy: lazyRoute(FlashcardDecks),
            handle: {
              chatRoute: 'flashcard-decks',
              ...osHandle('default', {
                windowGroup: 'flashcards',
                title: 'Flashcards',
              }),
            },
          },
          {
            path: 'flashcard-decks',
            element: <Navigate to="/flashcards" replace />,
          },
          {
            path: 'upload-deck',
            lazy: lazyRoute(UploadDeck),
            handle: osHandle('default', { title: 'Sync Anki' }),
          },
          {
            path: 'library',
            lazy: lazyRoute(LibraryHome),
            handle: {
              chatRoute: 'library',
              ...osHandle('default', {
                windowGroup: 'library',
                title: 'Library',
              }),
            },
          },
          {
            // Shares the library window group so opening an article crossfades
            // instead of replaying the window entry.
            path: 'library/:articleId',
            lazy: lazyRoute(Library),
            handle: {
              chatRoute: 'article',
              ...osHandle('default', {
                windowGroup: 'library',
                title: 'Library',
              }),
            },
          },
        ],
      },
      {
        path: 'topics',
        lazy: lazyRoute(Topics),
        handle: {
          chatRoute: 'topics',
          ...osHandle('default', { windowGroup: 'topics', title: 'Topics' }),
        },
      },
      {
        path: 'topics/:exam/:view',
        lazy: lazyRoute(Topics),
        handle: {
          chatRoute: 'topics',
          ...osHandle('default', { windowGroup: 'topics', title: 'Topics' }),
        },
      },
      {
        path: 'topics/:exam/:view/*',
        lazy: lazyRoute(Topics),
        handle: {
          chatRoute: 'topics',
          ...osHandle('default', { windowGroup: 'topics', title: 'Topics' }),
        },
      },
      {
        path: 'help',
        lazy: lazyRoute(Help),
        handle: {
          chatRoute: 'help',
          ...osHandle('default', { windowGroup: 'help', title: 'Help Docs' }),
        },
      },
      {
        // Shares the help window group so opening an article crossfades
        // instead of replaying the window entry.
        path: 'help/:slug',
        lazy: lazyRoute(Help),
        handle: {
          chatRoute: 'help',
          ...osHandle('default', { windowGroup: 'help', title: 'Help Docs' }),
        },
      },
      {
        // Pathless: no videos for an instance without a study library.
        element: <InstanceCapabilityRouteGuard />,
        children: [
          {
            // Pathless: an exam with videos off is sent home before the page mounts.
            element: <ExamFeatureRouteGuard />,
            children: [
              {
                path: 'videos',
                lazy: lazyRoute(Videos),
                handle: {
                  chatRoute: 'videos',
                  ...osHandle('default', {
                    windowGroup: 'videos',
                    title: 'Videos',
                  }),
                },
              },
              {
                path: 'videos/:videoId',
                lazy: lazyRoute(Videos),
                handle: {
                  chatRoute: 'videos',
                  ...osHandle('default', {
                    windowGroup: 'videos',
                    title: 'Videos',
                  }),
                },
              },
            ],
          },
        ],
      },
      {
        // Citation targets, old links and emails: the lecture opens on the
        // companion over the Dashboard, as View Document opens it.
        path: 'document/:lectureId',
        loader: documentRedirectLoader,
        element: null,
      },
      {
        // The desktop's old address; keep existing links working.
        path: 'chat',
        loader: chatRedirectLoader,
        element: null,
      },
      {
        path: 'chat-v2',
        loader: chatRedirectLoader,
        element: null,
      },
      {
        // Pathless: enforce the instance Games capability.
        element: <InstanceCapabilityRouteGuard />,
        children: [
          {
            // Pathless: an NCLEX primary is sent home before a game mounts.
            element: <ExamFeatureRouteGuard />,
            children: [
              {
                path: 'games',
                lazy: lazyRoute(Games),
                handle: {
                  chatRoute: 'games',
                  ...osHandle('default', { title: 'Games' }),
                },
              },
              {
                path: 'games/doctordle',
                lazy: lazyRoute(Doctordle),
                handle: {
                  chatRoute: 'games',
                  ...osHandle('default', { title: 'Doctordle' }),
                },
              },
              {
                path: 'games/doctordle/results',
                lazy: lazyRoute(DoctordleResults),
                handle: {
                  chatRoute: 'games',
                  ...osHandle('default', {
                    windowGroup: 'games/doctordle',
                    title: 'Doctordle',
                  }),
                },
              },
              {
                path: 'games/doctordle/archive',
                lazy: lazyRoute(DoctordleArchive),
                handle: {
                  chatRoute: 'games',
                  ...osHandle('default', {
                    windowGroup: 'games/doctordle',
                    title: 'Doctordle',
                  }),
                },
              },
              {
                path: 'games/daily-question',
                lazy: lazyRoute(Daily),
                handle: {
                  chatRoute: 'games',
                  ...osHandle('default', { title: 'Question of the Day' }),
                },
              },
              {
                path: 'games/start/pokemon-or-drug',
                lazy: lazyRoute(GameStart),
                handle: {
                  chatRoute: 'games',
                  ...osHandle('default', { title: 'Games' }),
                },
              },
              {
                path: 'games/start/:gameType',
                lazy: lazyRoute(GameStart),
                handle: {
                  chatRoute: 'games',
                  ...osHandle('default', { title: 'Games' }),
                },
              },
              {
                path: 'games/buzzwords',
                lazy: lazyRoute(Buzzwords),
                handle: {
                  chatRoute: 'games',
                  ...osHandle('default', { title: 'Buzzwords' }),
                },
              },
              {
                path: 'games/differential',
                lazy: lazyRoute(Differential),
                handle: {
                  chatRoute: 'games',
                  ...osHandle('default', { title: 'DDx Doc' }),
                },
              },
              {
                path: 'games/survival',
                lazy: lazyRoute(Survival),
                handle: {
                  chatRoute: 'games',
                  ...osHandle('default', { title: 'Survival' }),
                },
              },
              {
                path: 'games/pokemon-or-drug',
                lazy: lazyRoute(PokemonOrDrug),
                handle: {
                  chatRoute: 'games',
                  ...osHandle('default', { title: 'Pokémon or Drug' }),
                },
              },
            ],
          },
        ],
      },
      {
        // Pathless: no QBank start page for an instance without a study
        // library.
        element: <InstanceCapabilityRouteGuard />,
        children: [
          {
            path: 'qbank-start',
            lazy: lazyRoute(QBank),
            handle: {
              studyPhase: StudyPhase.HOME,
              chatRoute: 'qbank.start',
              ...osHandle('default', { title: 'QBank' }),
            },
          },
        ],
      },
      {
        // NCLEX-RN Format tour (ORA-1127): McGraw Hill instance only. The
        // page sends everyone else home.
        path: 'nclex/format-tour',
        lazy: lazyRoute(NclexFormatTour),
        handle: {
          studyPhase: StudyPhase.HOME,
          ...osHandle('default', { title: 'Format Tour' }),
        },
      },
      {
        // Audio Overviews library (ORA-735). The page itself handles the
        // feature being off for the account.
        path: 'audio-overviews',
        lazy: lazyRoute(AudioOverviews),
        handle: {
          chatRoute: 'audio-overviews',
          ...osHandle('default', {
            windowGroup: 'audio-overviews',
            title: 'Audio Overviews',
          }),
        },
      },
      {
        // Pathless: the self-assessment library's own pages. A course
        // assessment's slug and block routes below stay open.
        element: <InstanceCapabilityRouteGuard />,
        children: [
          {
            path: 'self-assessments',
            lazy: lazyRoute(SelfAssessments),
            handle: {
              chatRoute: 'self-assessments',
              ...osHandle('default', {
                windowGroup: 'self-assessments',
                title: 'Self-Assessments',
              }),
            },
          },
          {
            // Per-exam drill-in for multi-assessment exams. The literal `exam`
            // segment is more specific than `:assessmentSlug`, so it is matched
            // ahead of the dashboard route below. The key is the exam slug, or the
            // exam id of a school course (ORA-1126).
            path: 'self-assessments/exam/:examKey',
            lazy: lazyRoute(SelfAssessments),
            handle: {
              chatRoute: 'self-assessments',
              ...osHandle('default', {
                windowGroup: 'self-assessments',
                title: 'Self-Assessments',
              }),
            },
          },
        ],
      },
      {
        path: 'self-assessments/:assessmentSlug',
        lazy: lazyRoute(SelfAssessments),
        handle: {
          chatRoute: 'self-assessments',
          ...osHandle('default', {
            windowGroup: 'self-assessments',
            title: 'Self-Assessments',
          }),
        },
      },
      {
        path: 'self-assessments/:assessmentSlug/blocks/:blockNumber',
        lazy: lazyRoute(SelfAssessmentBlockRoute),
        handle: {
          chatRoute: 'self-assessment.block',
          ...osHandle('default', {
            windowGroup: 'self-assessments',
            title: 'Self-Assessments',
          }),
        },
      },
    ],
  },
  {
    path: 'webinar/:cohort',
    lazy: lazyRoute(WebinarRedirect),
    errorElement: <ErrorPage />,
  },
  {
    path: 'webinar/:cohort/admin',
    lazy: lazyRoute(WebinarAdminRedirect),
    errorElement: <ErrorPage />,
  },
  {
    path: 'email-preferences',
    lazy: lazyRoute(EmailPreferences),
    errorElement: <ErrorPage />,
  },
  {
    path: 'daily',
    element: <DeprecatedDailyRedirect />,
    errorElement: <ErrorPage />,
  },
  {
    path: 'verify-email',
    lazy: lazyRoute(VerifyEmail),
    errorElement: <ErrorPage />,
  },
  {
    path: 'verify-email/:token',
    lazy: lazyRoute(VerifyEmail),
    errorElement: <ErrorPage />,
  },
  {
    // Pathless: no pricing page for a student whose partner owns commerce;
    // a signed-out visitor is never redirected.
    element: <InstanceCapabilityRouteGuard />,
    children: [
      {
        path: 'pricing',
        element: <Pricing />,
        errorElement: <ErrorPage />,
      },
    ],
  },
  {
    path: 'pricing-redirect',
    element: <PricingRedirect />,
    errorElement: <ErrorPage />,
  },
  {
    path: 'auth',
    lazy: lazyRoute(Auth),
    // errorElement: <ErrorPage />,
  },
  {
    // Second step of a sign-in for a two-factor account (ORA-987).
    path: 'auth/2fa',
    lazy: lazyRoute(TwoFactor),
    errorElement: <ErrorPage />,
  },
  {
    // School-launch signup gate. Public; the page itself redirects to /auth
    // when the cohort has no matching school or is not unlock-activated.
    path: 'unlock/:cohortName',
    lazy: lazyRoute(Unlock),
    errorElement: <ErrorPage />,
  },
  {
    // Optional `:webinarSlug` segment lets a school host more than one webinar,
    // each at its own URL. The bare `/register/:cohort` resolves the school's
    // default webinar; `/register/:cohort/:webinarSlug` a specific one.
    path: 'register/:cohort/:webinarSlug?',
    lazy: lazyRoute(RegisterWebinar),
    errorElement: <ErrorPage />,
  },
  {
    // Public registration-count page for sharing with school ambassadors.
    path: 'registrations/:cohort/:webinarSlug?',
    lazy: lazyRoute(WebinarRegistrationCount),
    errorElement: <ErrorPage />,
  },
  {
    // Live-event trivia hub, keyed by the school's cohort string (same key
    // as /unlock). Public so Lunch & Learn attendees without accounts can
    // view the board and play as named guests.
    path: 'play/:school',
    lazy: lazyRoute(SchoolLeaderboard),
    errorElement: <ErrorPage />,
  },
  {
    path: 'play/:school/game',
    lazy: lazyRoute(GuestSurvival),
    errorElement: <ErrorPage />,
  },
  {
    path: 'faculty/auth',
    lazy: lazyRoute(Auth),
    errorElement: <ErrorPage />,
  },
  {
    path: 'faculty',
    element: (
      <ProtectedRoute authPath="/faculty/auth">
        <FacultyShell />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        lazy: lazyRoute(Faculty),
        handle: osHandle('default', {
          windowGroup: 'faculty',
          navMode: 'faculty',
          title: 'Faculty',
        }),
        children: [
          { index: true, lazy: lazyRoute(FacultyHome) },
          // Legacy faculty links collapse onto the single dashboard landing.
          {
            path: 'contract',
            element: <Navigate to="/faculty" replace />,
          },
          {
            path: 'contracts',
            element: <Navigate to="/faculty" replace />,
          },
          {
            path: 'cohorts',
            element: <Navigate to="/faculty" replace />,
          },
          {
            path: 'cohorts/:cohortId/members',
            lazy: lazyRoute(FacultyCohortMembers),
          },
          {
            path: 'cohorts/:cohortId/performance',
            lazy: lazyRoute(FacultyCohortPerformance),
          },
          {
            path: 'cohorts/:cohortId/members/:rosterEntryId',
            lazy: lazyRoute(FacultyStudentDetail),
          },
          {
            path: 'cohorts/:cohortId/members/:rosterEntryId/self-assessments/:assessmentSlug',
            lazy: lazyRoute(FacultyStudentSelfAssessmentResults),
          },
        ],
      },
    ],
  },
  {
    path: 'invite/:slug',
    lazy: lazyRoute(InvitePage),
    errorElement: <ErrorPage />,
  },
  {
    // LMS "Connect Account" landing (ORA-1046): the backend's LTI launch
    // redirects here. Public: the page runs its own fresh sign-in. The static
    // `error` path outranks `:pendingLaunchId`.
    path: 'lti/connect/error',
    lazy: lazyRoute(LtiConnect),
    errorElement: <ErrorPage />,
  },
  {
    path: 'lti/connect/:pendingLaunchId',
    lazy: lazyRoute(LtiConnect),
    errorElement: <ErrorPage />,
  },
  {
    path: 'reset',
    lazy: lazyRoute(ResetPassword),
    errorElement: <ErrorPage />,
  },
  ...devOnlyRoutes,
  {
    path: ':cohortId',
    element: <CohortRedirect />,
    errorElement: <ErrorPage />,
  },
]);
